import { Close, ShowChart, SwapVert } from "@mui/icons-material"
import {
    Box,
    Collapse,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    useTheme,
} from "@mui/material"
import { useMemo, useState, type ReactNode } from "react"
import { intl } from "src/internationalization/intl"
import { formatCurrencyBRL } from "src/lib/transformers/ClientsOverviewInfo"
import type {
    MovementPortfolioRollup,
    ObjectiveMovementSummary,
    OpportunityImpactedClient,
} from "src/services/api/opportunities/types"
import {
    normalizeAdvisorDisplayName,
    normalizeOpportunityAccountId,
    normalizePortfolioClientNameKey,
} from "src/services/api/opportunities/accountNormalize"
import type { Filter } from "../../basics/TabFilters/types"
import { BoxCard, Button, Grid, IconButton, TabFilters, Typography } from "../../basics"
import { getMonitoringTableContainerSx, getMonitoringTableSx } from "./monitoringTableStyles"

export interface ObjectiveMonitoringPanelProps {
    movementPortfolioRollups?: MovementPortfolioRollup[]
    movementSummary?: ObjectiveMovementSummary
    objectiveWarnings?: string[]
    impactedClients?: OpportunityImpactedClient[]
    /** inline = colapsável com botão; standalone = painel fixo abaixo da listagem. */
    variant?: "inline" | "standalone"
    /** Título da oportunidade (standalone). */
    objectiveTitle?: string
    /** Fechar painel standalone. */
    onClose?: () => void
    /** @deprecated Use variant="inline" */
    embedded?: boolean
    open?: boolean
    onOpenChange?: (open: boolean) => void
}

export interface ObjectiveMonitorTriggerProps {
    active?: boolean
    onClick: () => void
}

const ObjectiveMonitorTrigger = ({ active = false, onClick }: ObjectiveMonitorTriggerProps) => (
    <Button
        variant={active ? "contained" : "outlined"}
        color="primary"
        size="small"
        fullWidth
        startIcon={<ShowChart sx={{ fontSize: 18 }} />}
        onClick={onClick}
        sx={{
            minHeight: 32,
            fontSize: "0.8125rem",
            fontWeight: 600,
            textTransform: "none",
            "& .MuiButton-startIcon": { mr: 0.5 },
        }}
    >
        {active ? intl("objectiveMonitorCollapse") : intl("objectiveMonitorAction")}
    </Button>
)

type SortDirection = "desc" | "asc"

type GroupMode = "portfolio" | "advisor"

const UNASSIGNED_ADVISOR_KEY = "__unassigned__"

const normalizeAdvisorId = (raw: unknown): string | undefined => {
    if (raw === undefined || raw === null) return undefined
    const s = String(raw).trim()
    return s || undefined
}

const advisorGroupKey = (row: MovementPortfolioRollup): string => {
    const ia = row.investment_advisor
    if (!ia) return UNASSIGNED_ADVISOR_KEY
    const id = normalizeAdvisorId(ia.id) ?? normalizeAdvisorId(ia.element_id)
    if (id) return `id:${id}`
    const n = normalizeAdvisorDisplayName(ia.name)
    if (n) return `name:${n.toLocaleUpperCase("pt-BR")}`
    return UNASSIGNED_ADVISOR_KEY
}

type AdvisorAggRow = {
    groupKey: string
    advisorLabel: string
    opportunitySum: number
    liquidoSum: number
    percentRatio: number | null
}

const advisorPieceLabel = (row: MovementPortfolioRollup): string => {
    const ia = row.investment_advisor
    const name = normalizeAdvisorDisplayName(ia?.name)
    if (name) return name
    const id = normalizeAdvisorId(ia?.id)
    if (id) return id
    return ""
}

const aggregateByAdvisor = (rows: EnrichedRollup[]): AdvisorAggRow[] => {
    const m = new Map<string, { label: string; opp: number; liq: number }>()
    for (const er of rows) {
        const gk = advisorGroupKey(er.row)
        const opp =
            er.opportunityVolume != null && Number.isFinite(er.opportunityVolume)
                ? er.opportunityVolume
                : 0
        const net = Number(er.row.total_amount_net)
        const liq = Number.isFinite(net) ? net : 0
        const pieceLabel = advisorPieceLabel(er.row)
        const prev = m.get(gk)
        if (!prev) {
            m.set(gk, { label: pieceLabel, opp, liq })
        } else {
            const label = prev.label || pieceLabel
            m.set(gk, {
                label,
                opp: prev.opp + opp,
                liq: prev.liq + liq,
            })
        }
    }
    return Array.from(m.entries()).map(([groupKey, v]) => ({
        groupKey,
        advisorLabel: v.label,
        opportunitySum: v.opp,
        liquidoSum: v.liq,
        percentRatio: v.opp > 0 ? v.liq / v.opp : null,
    }))
}

const sortAdvisorRows = (rows: AdvisorAggRow[], direction: SortDirection): AdvisorAggRow[] => {
    const copy = [...rows]
    copy.sort((a, b) => {
        const na = a.percentRatio === null
        const nb = b.percentRatio === null
        if (na && nb) return a.advisorLabel.localeCompare(b.advisorLabel, "pt-BR")
        if (na) return 1
        if (nb) return -1
        const cmp = a.percentRatio! - b.percentRatio!
        return direction === "desc" ? -cmp : cmp
    })
    return copy
}

const volumeByImpactedAccount = (impacted: OpportunityImpactedClient[] | undefined): Map<string, number> => {
    const m = new Map<string, number>()
    if (!impacted?.length) return m
    for (const ic of impacted) {
        const key = normalizeOpportunityAccountId(ic?.account ?? ic?.p?.account)
        if (!key) continue
        const vol = Number(ic.volumeOportunidade)
        if (!Number.isFinite(vol)) continue
        m.set(key, vol)
    }
    return m
}

/** When exactly one impacted row uses this display name, map name → volume (account mismatch fallback). */
const volumeByImpactedUniqueName = (impacted: OpportunityImpactedClient[] | undefined): Map<string, number> => {
    const m = new Map<string, number>()
    if (!impacted?.length) return m
    const counts = new Map<string, number>()
    for (const ic of impacted) {
        const nk = normalizePortfolioClientNameKey(ic?.name ?? ic?.p?.name)
        if (!nk) continue
        counts.set(nk, (counts.get(nk) ?? 0) + 1)
    }
    for (const ic of impacted) {
        const nk = normalizePortfolioClientNameKey(ic?.name ?? ic?.p?.name)
        if (!nk || counts.get(nk) !== 1) continue
        const vol = Number(ic.volumeOportunidade)
        if (!Number.isFinite(vol)) continue
        m.set(nk, vol)
    }
    return m
}

const rollupAccountKey = (row: MovementPortfolioRollup): string =>
    normalizeOpportunityAccountId(row.portfolio_account ?? row.account)

const formatPercentRatio = (ratio: number): string =>
    new Intl.NumberFormat("pt-BR", {
        style: "percent",
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
    }).format(ratio)

type EnrichedRollup = {
    row: MovementPortfolioRollup
    accountKey: string
    opportunityVolume: number | null
    percentRatio: number | null
    /** Carteira só em Ver Clientes — sem linha no rollup de movimentos */
    impactedOnly?: boolean
}

const enrichRollups = (
    rollups: MovementPortfolioRollup[] | undefined,
    impacted: OpportunityImpactedClient[] | undefined
): EnrichedRollup[] => {
    const oppMap = volumeByImpactedAccount(impacted)
    const nameMap = volumeByImpactedUniqueName(impacted)
    const list = rollups ?? []
    return list.map((row) => {
        const accountKey = rollupAccountKey(row)
        let oppVol = accountKey ? oppMap.get(accountKey) : undefined
        let matched = oppVol !== undefined && Number.isFinite(oppVol)

        if (!matched && row.portfolio_name) {
            const nk = normalizePortfolioClientNameKey(row.portfolio_name)
            const byName = nk ? nameMap.get(nk) : undefined
            if (byName !== undefined && Number.isFinite(byName)) {
                oppVol = byName
                matched = true
            }
        }

        const liquido = Number(row.total_amount_net)

        let percentRatio: number | null = null
        if (matched && oppVol! > 0 && Number.isFinite(liquido)) {
            percentRatio = liquido / oppVol!
        }

        return {
            row,
            accountKey,
            opportunityVolume: matched ? oppVol! : null,
            percentRatio,
        }
    })
}

/** Impacted clients (Ver Clientes) whose account never appears on a movement rollup row — líquido 0; assessor N/A. Valor oportunidade = soma Ver Clientes para a conta. */
const enrichImpactedWithoutRollup = (
    impacted: OpportunityImpactedClient[] | undefined,
    rollups: MovementPortfolioRollup[] | undefined
): EnrichedRollup[] => {
    const rollupKeys = new Set<string>()
    for (const r of rollups ?? []) {
        const k = rollupAccountKey(r)
        if (k) rollupKeys.add(k)
    }

    type ImpactedAgg = {
        volumeSum: number
        labelName: string
        advisorNames: Set<string>
        advisorIds: Set<string>
        advisorElementIds: Set<string>
    }

    const agg = new Map<string, ImpactedAgg>()
    for (const ic of impacted ?? []) {
        const k = normalizeOpportunityAccountId(ic?.account ?? ic?.p?.account)
        if (!k || rollupKeys.has(k)) continue
        const vol = Number(ic.volumeOportunidade)
        const name = String(ic?.name ?? ic?.p?.name ?? "").trim() || "—"
        const addVol = Number.isFinite(vol) ? vol : 0
        const adv = normalizeAdvisorDisplayName(ic?.advisor_name)
        const advId = normalizeAdvisorId(ic?.advisor_id)
        const advElementId = normalizeAdvisorId(ic?.advisor_element_id)
        const prev = agg.get(k)
        if (prev) {
            const advisorNames = new Set(prev.advisorNames)
            const advisorIds = new Set(prev.advisorIds)
            const advisorElementIds = new Set(prev.advisorElementIds)
            if (adv) advisorNames.add(adv)
            if (advId) advisorIds.add(advId)
            if (advElementId) advisorElementIds.add(advElementId)
            agg.set(k, {
                volumeSum: prev.volumeSum + addVol,
                labelName: prev.labelName,
                advisorNames,
                advisorIds,
                advisorElementIds,
            })
        } else {
            const advisorNames = new Set<string>()
            const advisorIds = new Set<string>()
            const advisorElementIds = new Set<string>()
            if (adv) advisorNames.add(adv)
            if (advId) advisorIds.add(advId)
            if (advElementId) advisorElementIds.add(advElementId)
            agg.set(k, {
                volumeSum: addVol,
                labelName: name,
                advisorNames,
                advisorIds,
                advisorElementIds,
            })
        }
    }

    const out: EnrichedRollup[] = []
    for (const [
        accountKey,
        { volumeSum, labelName, advisorNames, advisorIds, advisorElementIds },
    ] of Array.from(agg.entries())) {
        let percentRatio: number | null = null
        if (volumeSum > 0) {
            percentRatio = 0
        }
        const advisorsSorted = Array.from(advisorNames).sort((a, b) => a.localeCompare(b, "pt-BR"))
        const advisorLabel =
            advisorsSorted.length === 0
                ? undefined
                : advisorsSorted.length === 1
                  ? advisorsSorted[0]
                  : advisorsSorted.join(" · ")
        const singleAdvisorId = advisorIds.size === 1 ? Array.from(advisorIds)[0] : undefined
        const singleAdvisorElementId =
            advisorElementIds.size === 1 ? Array.from(advisorElementIds)[0] : undefined
        const canonicalAdvisorId = singleAdvisorId ?? singleAdvisorElementId
        out.push({
            impactedOnly: true,
            accountKey,
            opportunityVolume: volumeSum,
            percentRatio,
            row: {
                portfolio_account: accountKey,
                account: accountKey,
                total_amount_net: 0,
                portfolio_name: labelName,
                investment_advisor: advisorLabel
                    ? {
                          id: canonicalAdvisorId,
                          element_id: singleAdvisorElementId,
                          name: advisorLabel,
                      }
                    : null,
            },
        })
    }
    return out
}

const sortEnriched = (rows: EnrichedRollup[], direction: SortDirection): EnrichedRollup[] => {
    const copy = [...rows]
    copy.sort((a, b) => {
        const na = a.percentRatio === null
        const nb = b.percentRatio === null
        if (na && nb) return a.accountKey.localeCompare(b.accountKey)
        if (na) return 1
        if (nb) return -1
        const cmp = a.percentRatio! - b.percentRatio!
        return direction === "desc" ? -cmp : cmp
    })
    return copy
}

const ObjectiveMonitoringPanel = ({
    movementPortfolioRollups,
    movementSummary,
    objectiveWarnings,
    impactedClients,
    variant = "inline",
    objectiveTitle,
    onClose,
    embedded = false,
    open: openControlled,
    onOpenChange,
}: ObjectiveMonitoringPanelProps) => {
    const resolvedVariant = variant === "standalone" ? "standalone" : "inline"
    const [openInternal, setOpenInternal] = useState(false)
    const open = resolvedVariant === "standalone" ? true : (openControlled ?? openInternal)
    const setOpen = (next: boolean | ((prev: boolean) => boolean)) => {
        const value = typeof next === "function" ? next(open) : next
        if (onOpenChange) onOpenChange(value)
        else setOpenInternal(value)
    }
    const [sortDirection, setSortDirection] = useState<SortDirection>("desc")
    const [groupMode, setGroupMode] = useState<GroupMode>("portfolio")

    const summaryTotal =
        movementSummary && typeof movementSummary.total_amount_net === "number"
            ? movementSummary.total_amount_net
            : movementSummary && typeof movementSummary.grand_total_amount_net === "number"
              ? movementSummary.grand_total_amount_net
              : undefined

    const mergedFlat = useMemo(() => {
        const enriched = enrichRollups(movementPortfolioRollups, impactedClients)
        const impactedOnly = enrichImpactedWithoutRollup(impactedClients, movementPortfolioRollups)
        return [...enriched, ...impactedOnly]
    }, [movementPortfolioRollups, impactedClients])

    const sortedPortfolioRows = useMemo(
        () => sortEnriched(mergedFlat, sortDirection),
        [mergedFlat, sortDirection]
    )

    const advisorAggregated = useMemo(() => aggregateByAdvisor(mergedFlat), [mergedFlat])

    const sortedAdvisorRows = useMemo(
        () => sortAdvisorRows(advisorAggregated, sortDirection),
        [advisorAggregated, sortDirection]
    )

    const showMonitoringTable = mergedFlat.length > 0
    const theme = useTheme()
    const tableSx = useMemo(() => getMonitoringTableSx(theme), [theme])
    const tableContainerSx = useMemo(() => getMonitoringTableContainerSx(theme), [theme])

    const groupFilters = useMemo<Filter[]>(
        () => [
            {
                id: "portfolio",
                label: intl("objectiveMonitoringGroupModePortfolio"),
                value: "portfolio",
            },
            {
                id: "advisor",
                label: intl("objectiveMonitoringGroupModeAdvisor"),
                value: "advisor",
            },
        ],
        []
    )
    const selectedGroupFilter =
        groupFilters.find((f) => f.id === groupMode) ?? groupFilters[0]

    const headerCell = (label: string, align: "left" | "right" = "left") => (
        <TableCell align={align} sx={{ whiteSpace: "nowrap" }}>
            <Typography variant="caption" color="secondary" component="span">
                {label}
            </Typography>
        </TableCell>
    )

    const bodyCell = (content: ReactNode, align: "left" | "right" = "left", maxWidth?: number) => (
        <TableCell align={align} sx={{ whiteSpace: "nowrap", maxWidth }}>
            <Typography variant="body2" component="span">
                {content}
            </Typography>
        </TableCell>
    )

    const panelBody = (
        <BoxCard sx={{ mt: resolvedVariant === "standalone" ? 0 : 1, p: 2 }}>
            {resolvedVariant === "standalone" && (
                <Grid container justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                    <Grid item sx={{ pr: 1, flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" color="secondary">
                            {intl("objectiveMonitoringBadge")}
                        </Typography>
                        <Typography variant="h3" sx={{ mt: 0.25, lineHeight: 1.25 }}>
                            {objectiveTitle || "—"}
                        </Typography>
                    </Grid>
                    {onClose ? (
                        <IconButton
                            size="small"
                            color="neutral"
                            variant="clean"
                            aria-label={intl("objectiveMonitorCollapse")}
                            onClick={onClose}
                        >
                            <Close fontSize="small" />
                        </IconButton>
                    ) : null}
                </Grid>
            )}
            {typeof summaryTotal === "number" && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="secondary">
                                {intl("objectiveMonitoringSummary")}
                            </Typography>
                            <Typography variant="h3" sx={{ mt: 0.25 }}>
                                {formatCurrencyBRL(summaryTotal)}
                            </Typography>
                            {typeof movementSummary?.portfolio_count === "number" &&
                                typeof movementSummary?.movement_count === "number" && (
                                    <Typography variant="caption" color="secondary" sx={{ display: "block", mt: 0.5 }}>
                                        {intl("objectiveMonitoringMeta", {
                                            values: {
                                                portfolioCount: String(movementSummary.portfolio_count),
                                                movementCount: String(movementSummary.movement_count),
                                            },
                                        })}
                                    </Typography>
                                )}
                        </Box>
                    )}

                    {showMonitoringTable ? (
                        <>
                            <Grid
                                container
                                justifyContent="space-between"
                                alignItems="center"
                                flexWrap="wrap"
                                gap={1}
                                sx={{ mb: 1.5 }}
                            >
                                <TabFilters
                                    filters={groupFilters}
                                    selectedFilter={selectedGroupFilter}
                                    onFilterChange={(f) => setGroupMode(f.id as GroupMode)}
                                />
                                <Grid container alignItems="center" gap={1}>
                                    <Typography variant="caption" color="secondary">
                                        {sortDirection === "desc"
                                            ? intl("objectiveMonitoringSortHintDesc")
                                            : intl("objectiveMonitoringSortHintAsc")}
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        variant="clean"
                                        aria-label={intl("objectiveMonitoringSortToggleAria")}
                                        onClick={() =>
                                            setSortDirection((d) => (d === "desc" ? "asc" : "desc"))
                                        }
                                    >
                                        <SwapVert fontSize="small" />
                                    </IconButton>
                                </Grid>
                            </Grid>
                            <TableContainer sx={tableContainerSx}>
                                <Table size="small" sx={tableSx}>
                                    <TableHead>
                                        <TableRow>
                                            {headerCell(
                                                groupMode === "portfolio"
                                                    ? intl("objectiveMonitoringPortfolioCol")
                                                    : intl("objectiveMonitoringAdvisorCol")
                                            )}
                                            {groupMode === "portfolio" ? (
                                                headerCell(intl("objectiveMonitoringAdvisorCol"))
                                            ) : (
                                                <TableCell sx={{ display: "none" }} />
                                            )}
                                            {headerCell(
                                                intl("objectiveMonitoringOpportunityTotalCol"),
                                                "right"
                                            )}
                                            {headerCell(intl("objectiveMonitoringAmountCol"), "right")}
                                            {headerCell(
                                                intl("objectiveMonitoringPercentDoneCol"),
                                                "right"
                                            )}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {groupMode === "portfolio"
                                            ? sortedPortfolioRows.map(
                                                  (
                                                      {
                                                          row,
                                                          percentRatio,
                                                          opportunityVolume,
                                                          impactedOnly,
                                                      },
                                                      idx
                                                  ) => {
                                                      const account =
                                                          row.portfolio_account ?? row.account ?? "—"
                                                      const portfolioLabel =
                                                          row.portfolio_name && String(account) !== "—"
                                                              ? `${row.portfolio_name} (${String(account)})`
                                                              : String(account)
                                                      const advisorCell =
                                                          row.investment_advisor?.name?.trim() ||
                                                          (impactedOnly
                                                              ? intl("objectiveMonitoringNA")
                                                              : intl("objectiveMonitoringNoAdvisor"))
                                                      return (
                                                          <TableRow
                                                              key={`${impactedOnly ? "i" : "r"}-${String(account)}-${idx}`}
                                                          >
                                                              {bodyCell(portfolioLabel, "left", 200)}
                                                              {bodyCell(advisorCell, "left", 140)}
                                                              {bodyCell(
                                                                  opportunityVolume !== null
                                                                      ? formatCurrencyBRL(opportunityVolume)
                                                                      : intl("objectiveMonitoringNA"),
                                                                  "right"
                                                              )}
                                                              {bodyCell(
                                                                  formatCurrencyBRL(row.total_amount_net),
                                                                  "right"
                                                              )}
                                                              {bodyCell(
                                                                  percentRatio !== null
                                                                      ? formatPercentRatio(percentRatio)
                                                                      : intl("objectiveMonitoringNA"),
                                                                  "right"
                                                              )}
                                                          </TableRow>
                                                      )
                                                  }
                                              )
                                            : sortedAdvisorRows.map((ar, idx) => {
                                                  const nameCell =
                                                      ar.groupKey === UNASSIGNED_ADVISOR_KEY
                                                          ? intl("objectiveMonitoringNoAdvisor")
                                                          : ar.advisorLabel ||
                                                            ar.groupKey.replace(/^id:/, "") ||
                                                            intl("objectiveMonitoringNoAdvisor")
                                                  return (
                                                      <TableRow key={`adv-${ar.groupKey}-${idx}`}>
                                                          {bodyCell(nameCell, "left", 240)}
                                                          <TableCell
                                                              sx={{
                                                                  display: "none",
                                                                  maxWidth: 140,
                                                              }}
                                                          />
                                                          {bodyCell(
                                                              formatCurrencyBRL(ar.opportunitySum),
                                                              "right"
                                                          )}
                                                          {bodyCell(
                                                              formatCurrencyBRL(ar.liquidoSum),
                                                              "right"
                                                          )}
                                                          {bodyCell(
                                                              ar.percentRatio !== null
                                                                  ? formatPercentRatio(ar.percentRatio)
                                                                  : intl("objectiveMonitoringNA"),
                                                              "right"
                                                          )}
                                                      </TableRow>
                                                  )
                                              })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    ) : (
                        <Typography variant="caption" color="secondary">
                            {intl("objectiveMonitoringEmpty")}
                        </Typography>
                    )}

                    {objectiveWarnings && objectiveWarnings.length > 0 && (
                        <Typography
                            variant="caption"
                            sx={{ mt: 1.5, display: "block", color: "warning.main" }}
                        >
                            {objectiveWarnings.join(" · ")}
                        </Typography>
                    )}
        </BoxCard>
    )

    if (resolvedVariant === "standalone") {
        return <Box sx={{ width: "100%" }}>{panelBody}</Box>
    }

    return (
        <Box sx={{ width: "100%", mt: embedded ? 0 : 0.5 }}>
            <Button
                variant="text"
                color="primary"
                size="small"
                startIcon={<ShowChart sx={{ fontSize: 18 }} />}
                onClick={() => setOpen(!open)}
                sx={{
                    minHeight: 28,
                    px: embedded ? 0 : 0.5,
                    py: 0,
                    fontSize: "0.8125rem",
                    fontWeight: 600,
                    textTransform: "none",
                    justifyContent: "flex-start",
                    "& .MuiButton-startIcon": { mr: 0.5 },
                }}
            >
                {open ? intl("objectiveMonitorCollapse") : intl("objectiveMonitorAction")}
            </Button>

            <Collapse in={open} timeout="auto" unmountOnExit>
                {panelBody}
            </Collapse>
        </Box>
    )
}

export { ObjectiveMonitoringPanel, ObjectiveMonitorTrigger }
